import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Award, Wallet, CreditCard, Play, Trophy, ShieldCheck, Copy, UserPlus, Shuffle, Gift, ChevronDown, Ticket, Grid3x3, Clock, ZoomIn, Zap, ExternalLink, Trash2 } from "lucide-react";
import { api, apiError, fileUrl, certUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveSocket } from "@/lib/useLiveSocket";
import { Loader, StatusBadge, GradeBadge, Lightbox } from "@/components/common";

export default function DropDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [drop, setDrop] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refLink, setRefLink] = useState("");
  const [randomQty, setRandomQty] = useState(1);
  const [entrants, setEntrants] = useState([]);
  const [showAllEntrants, setShowAllEntrants] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [pickMode, setPickMode] = useState("quick");
  const [now, setNow] = useState(Date.now());
  const [lightbox, setLightbox] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [reveal, setReveal] = useState(null);

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const load = useCallback(() => {
    api.get(`/drops/${id}`).then(({ data }) => setDrop(data)).catch(() => setDrop(false));
    api.get(`/drops/${id}/entrants`).then(({ data }) => setEntrants(data.entrants || [])).catch(() => {});
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const removeEntry = async (userId, refund = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/drops/${id}/remove-entry`, { user_id: userId, refund });
      toast.success(`Removed ${data.removed} seat${data.removed > 1 ? "s" : ""}${data.refunded ? ` · $${data.refunded.toFixed(2)} refunded as credit` : ""}`);
      setConfirmRemove(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  useEffect(() => {
    if (drop?.is_referral && user) api.get("/auth/referral").then(({ data }) => setRefLink(`${window.location.origin}/?ref=${data.code}`)).catch(() => {});
  }, [drop, user]);

  useLiveSocket(useCallback((evt) => {
    if (evt.type === "drop_update" && String(evt.drop_id) === String(id)) load();
  }, [id, load]));

  if (drop === null) return <Loader />;
  if (drop === false) return <div className="text-center py-24 text-slate-400">Drop not found.</div>;

  const isAdmin = user?.role === "admin";
  const free = drop.is_giveaway;
  const availableSeats = (drop.seats || []).filter((s) => s.status === "available").length;
  const mineCount = (drop.seats || []).filter((s) => s.mine).length;
  const qtyCap = drop.max_per_user ? Math.min(availableSeats, Math.max(0, drop.max_per_user - mineCount)) : availableSeats;
  const maxQty = Math.max(1, qtyCap);
  const qty = Math.min(Math.max(1, randomQty), maxQty);
  const toggleSeat = (s) => {
    if (s.status !== "available") return;
    if (drop.max_per_user) {
      const mine = drop.seats.filter((x) => x.mine).length;
      if (!selected.includes(s.n) && selected.length + mine >= drop.max_per_user) {
        toast.error(`Max ${drop.max_per_user} seats per user`); return;
      }
    }
    setSelected((prev) => prev.includes(s.n) ? prev.filter((n) => n !== s.n) : [...prev, s.n]);
  };

  const total = selected.length * drop.ticket_price;
  const charged = total;

  // Open the commit/confirmation step before any free status is known
  const askConfirm = (useCredit, randomCount) => {
    if (!user) { nav("/login"); return; }
    const count = randomCount || selected.length;
    if (!count) { toast.error("Select at least one seat"); return; }
    setConfirm({ useCredit, randomCount: randomCount || 0, count });
  };

  const runCheckout = async () => {    if (!confirm) return;
    const { useCredit, randomCount } = confirm;
    setBusy(true);
    try {
      const payload = { seat_numbers: randomCount ? [] : selected, origin_url: window.location.origin, use_credit: useCredit };
      if (randomCount) payload.random_count = randomCount;
      const { data } = await api.post(`/drops/${id}/checkout`, payload);
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      setConfirm(null);
      setSelected([]);
      await refresh();
      load();
      if (data.free_seats && data.free_seats.length) {
        setReveal({ seats: data.free_seats });
      } else {
        toast.success(data.message || "Seats claimed!");
      }
    } catch (err) {
      const msg = apiError(err);
      setConfirm(null);
      if (msg === "SHIPPING_REQUIRED") { toast.error("Add your real name & shipping address first"); nav("/account"); }
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  const runDraw = async () => {
    nav(`/drops/${id}/draw`);
  };

  const forceFull = async () => {
    if (!window.confirm("Mark this play as full and open the live draw now, even though it isn't full?")) return;
    setBusy(true);
    try { await api.post(`/drops/${id}/force-full`); toast.success("Ready to draw — opening the live spin"); nav(`/drops/${id}/draw`); }
    catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  const copyRef = () => { navigator.clipboard.writeText(refLink); toast.success("Invite link copied!"); };

  const remain = (isoStr) => {
    if (!isoStr) return null;
    const ms = new Date(isoStr).getTime() - now;
    if (ms <= 0) return null;
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  const seatColor = (s) => {    if (selected.includes(s.n)) return "gold-gradient text-obsidian gold-glow scale-105";
    if (s.mine) return "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40";
    if (s.status === "claimed") return "bg-vault text-slate-600 cursor-not-allowed";
    if (s.status === "held") return "bg-amber-500/10 text-amber-500/60 cursor-not-allowed";
    return "card-surface text-slate-200 hover:border-gold/50";
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Gallery */}
        <div>
          <button type="button" onClick={() => drop.prize_photo && setLightbox(fileUrl(drop.prize_photo))} className="group relative block w-full rounded-2xl overflow-hidden card-surface aspect-square gold-glow" data-testid="drop-prize-photo-btn">
            {drop.prize_photo ? <img src={fileUrl(drop.prize_photo)} alt={drop.title} className="w-full h-full object-cover" data-testid="drop-prize-photo" /> : <div className="w-full h-full flex items-center justify-center text-slate-600"><Award className="w-16 h-16" /></div>}
            {drop.prize_photo && <span className="absolute bottom-3 right-3 flex items-center gap-1.5 text-xs font-semibold text-white bg-black/50 backdrop-blur px-2.5 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn className="w-3.5 h-3.5" /> View</span>}
          </button>
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <StatusBadge status={drop.status} />
            <GradeBadge grade={[drop.brand, drop.grade].filter(Boolean).join(" ")} />
          </div>
          <h1 className="font-display text-3xl font-black text-white" data-testid="drop-title">{drop.title}</h1>
          {drop.description && <p className="text-slate-300 mt-3 leading-relaxed">{drop.description}</p>}

          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="card-surface rounded-xl p-3"><p className="text-slate-500 text-xs">{drop.is_referral ? "Entry" : "Per seat"}</p><p className="text-gold font-mono font-bold text-lg">{drop.is_referral ? "Invite" : free ? "FREE" : `$${drop.ticket_price.toFixed(2)}`}</p></div>
            <div className="card-surface rounded-xl p-3"><p className="text-slate-500 text-xs">{drop.is_referral ? "Entries" : "Seats"}</p><p className="text-white font-mono font-bold text-lg flex items-center gap-1"><Users className="w-4 h-4 text-gold" />{drop.is_referral ? `${drop.referral_entries || 0}/${drop.total_seats}` : `${drop.claimed_seats}/${drop.total_seats}`}</p></div>
            <div className="card-surface rounded-xl p-3"><p className="text-slate-500 text-xs">Winners</p><p className="text-white font-mono font-bold text-lg">{drop.num_winners}</p></div>
          </div>
          {drop.certificate_number && (
            <p className="mt-3 text-xs font-mono text-slate-500">CERT {certUrl(drop.brand, drop.certificate_number)
              ? <a href={certUrl(drop.brand, drop.certificate_number)} target="_blank" rel="noopener noreferrer" className="text-gold hover:text-gold-bright hover:underline inline-flex items-center gap-1" data-testid="drop-cert-link">#{drop.certificate_number} <ExternalLink className="w-3 h-3" /></a>
              : <span>#{drop.certificate_number}</span>}
            </p>
          )}
          {drop.certificate_number && certUrl(drop.brand, drop.certificate_number) && <p className="text-[10px] text-slate-500 mt-1">Tap the cert # to verify on {drop.brand}'s official site.</p>}
          {drop.non_winners_only && (
            <div className="mt-4 flex items-start gap-2.5 rounded-xl p-3 border border-gold/25 bg-gold/5" data-testid="nonwinners-banner">
              <Trophy className="w-4 h-4 text-gold shrink-0 mt-0.5" />
              <p className="text-xs text-slate-300"><b className="text-gold">Non-winners only.</b> This giveaway is reserved for members who haven't won a draw yet. If you've already won before, you won't be able to enter this one.</p>
            </div>
          )}

          {/* Draw status / winners */}
          {drop.status === "drawn" && (
            <div className="mt-5" data-testid="inline-winners">
              <h3 className="font-display font-bold text-white flex items-center gap-2"><Trophy className="w-5 h-5 text-gold" /> Winner{drop.winners.length > 1 ? "s" : ""}</h3>
              <div className="mt-2 space-y-2">
                {drop.winners.map((w, i) => (
                  <div key={i} className="card-surface rounded-xl p-3 flex items-center justify-between" data-testid={`inline-winner-${i}`}>
                    <span className="text-white font-semibold">🏆 {w.name}</span>
                    {w.seat && <span className="font-mono text-gold text-sm">Seat #{w.seat}</span>}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-4">
                {false && drop.has_proof && <Link to={`/verify/${id}`} className="text-gold text-sm font-semibold flex items-center gap-1" data-testid="verify-link"><ShieldCheck className="w-4 h-4" /> Verify draw</Link>}
                <Link to={`/drops/${id}/draw`} className="text-slate-400 text-sm hover:text-gold flex items-center gap-1" data-testid="watch-replay"><Play className="w-4 h-4" /> Watch replay</Link>
              </div>
            </div>
          )}
          {drop.status === "drawing" && !isAdmin && (
            <Link to={`/drops/${id}/draw`} className="mt-5 card-surface rounded-xl px-4 py-3 flex items-center gap-2 text-white font-semibold hover:border-gold/40 inline-flex" data-testid="view-draw">
              <Trophy className="w-5 h-5 text-gold" /> Go to the live draw
            </Link>
          )}

          {/* Admin controls */}
          {isAdmin && drop.status === "active" && !drop.is_referral && entrants.length > 0 && (
            <button onClick={forceFull} disabled={busy} className="mt-4 w-full border border-gold/40 text-gold font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-gold/10" data-testid="force-full-button">
              <Zap className="w-5 h-5" /> Mark full & draw now
            </button>
          )}
          {isAdmin && (drop.status === "drawing" || drop.status === "full") && (
            <button onClick={runDraw} disabled={busy} className="mt-4 w-full gold-gradient text-obsidian font-bold py-3 rounded-xl flex items-center justify-center gap-2" data-testid="run-draw-button">
              <Play className="w-5 h-5" /> Go to the live draw & spin
            </button>
          )}
        </div>
      </div>

      {/* Referral giveaway */}
      {drop.is_referral && (
        <div className="mt-10 card-surface rounded-2xl p-6" data-testid="referral-section">
          <div className="flex items-center gap-3 mb-3"><UserPlus className="w-6 h-6 text-gold" /><h2 className="font-display text-xl font-bold text-white">Referral Giveaway</h2></div>
          <p className="text-slate-300">Earn <b className="text-gold">1 entry</b> for every friend who signs up with your invite link while this giveaway is open. More friends = better odds. It fills at {drop.total_seats} entries, then the host draws.</p>
          <p className="mt-2 text-sm text-slate-400">Current entries: <b className="text-gold font-mono">{drop.referral_entries ?? 0}</b> / {drop.total_seats}</p>
          {user ? (
            <div className="mt-4 flex gap-2">
              <input readOnly value={refLink} className="flex-1 bg-vault border border-gold/20 rounded-xl px-4 py-3 text-slate-300 font-mono text-sm" data-testid="referral-input" />
              <button onClick={copyRef} className="gold-gradient text-obsidian font-bold px-4 rounded-xl flex items-center gap-2" data-testid="copy-referral"><Copy className="w-4 h-4" /> Copy</button>
            </div>
          ) : <Link to="/login" className="mt-4 inline-block text-gold font-semibold">Log in to get your invite link →</Link>}
        </div>
      )}

      {/* Seat selection / entry */}
      {!drop.is_referral && drop.status === "active" && (
        <div className="mt-10">
          <div className="mb-6" data-testid="sold-progress">
            <div className="h-2.5 rounded-full bg-vault overflow-hidden border border-gold/10">
              <div className="h-full gold-gradient transition-[width] duration-500" style={{ width: `${drop.total_seats ? Math.round((drop.claimed_seats / drop.total_seats) * 100) : 0}%` }} />
            </div>
            <div className="mt-1.5 flex justify-between text-xs text-slate-400 font-mono">
              <span>{drop.claimed_seats} taken</span>
              <span className="text-gold">{drop.total_seats ? Math.round((drop.claimed_seats / drop.total_seats) * 100) : 0}% filled</span>
              <span>{drop.total_seats - drop.claimed_seats} left</span>
            </div>
          </div>

          {/* Held-seat countdown (single) */}
          {(() => {
            const mine = (drop.seats || []).filter((s) => s.status === "held" && s.mine && remain(s.hold_until));
            if (!mine.length) return null;
            const soonest = mine.reduce((a, b) => (new Date(a.hold_until) < new Date(b.hold_until) ? a : b));
            return (
              <div className="mb-5 rounded-2xl p-4 border border-amber-400/25 bg-amber-500/5 flex items-center gap-3" data-testid="hold-countdown">
                <Clock className="w-5 h-5 text-amber-300 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-300">Your {mine.length} seat{mine.length > 1 ? "s are" : " is"} reserved</p>
                  <p className="text-xs text-slate-400">Frees up in <span className="font-mono text-amber-200" data-testid="hold-timer">{remain(soonest.hold_until)}</span> if not paid</p>
                </div>
              </div>
            );
          })()}

          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2 p-1.5 rounded-2xl bg-vault/60 border border-gold/15 mb-5" data-testid="pick-mode-toggle">
            <button onClick={() => setPickMode("quick")} data-testid="mode-quick"
              className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-colors ${pickMode === "quick" ? "gold-gradient text-obsidian gold-glow" : "text-slate-300 hover:text-white"}`}>
              <Shuffle className="w-4 h-4" /> Quick Pick
            </button>
            <button onClick={() => setPickMode("seats")} data-testid="mode-seats"
              className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-colors ${pickMode === "seats" ? "gold-gradient text-obsidian gold-glow" : "text-slate-300 hover:text-white"}`}>
              <Grid3x3 className="w-4 h-4" /> Pick Seats
            </button>
          </div>

          {pickMode === "quick" ? (
            /* Quick Pick */
            <div className="rounded-2xl p-6 relative overflow-hidden border border-gold/25 bg-gradient-to-br from-gold/10 via-vault to-vault" data-testid="quick-pick">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl gold-gradient flex items-center justify-center text-obsidian shrink-0"><Shuffle className="w-5 h-5" /></div>
                  <div>
                    <p className="font-display font-bold text-white text-lg">Feeling lucky?</p>
                    <p className="text-xs text-slate-400">Skip the grid — we'll grab open seats at random for you.</p>
                  </div>
                </div>

                <div className="rounded-2xl bg-obsidian/40 border border-gold/10 p-4">
                  <p className="text-[11px] uppercase tracking-widest text-slate-500 text-center mb-3">How many seats?</p>
                  <div className="flex items-center justify-center gap-6">
                    <button type="button" onClick={() => setRandomQty(Math.max(1, qty - 1))} disabled={qty <= 1} className="w-12 h-12 rounded-xl bg-obsidian/60 border border-gold/25 text-gold text-2xl font-bold hover:bg-gold/10 flex items-center justify-center disabled:opacity-40" data-testid="random-minus">−</button>
                    <input type="number" min="1" max={maxQty} value={qty} onChange={(e) => setRandomQty(Math.min(maxQty, Math.max(1, parseInt(e.target.value) || 1)))} className="w-20 bg-transparent text-white text-center text-4xl font-display font-black focus:outline-none" data-testid="random-qty" />
                    <button type="button" onClick={() => setRandomQty(Math.min(maxQty, qty + 1))} disabled={qty >= maxQty} className="w-12 h-12 rounded-xl bg-obsidian/60 border border-gold/25 text-gold text-2xl font-bold hover:bg-gold/10 flex items-center justify-center disabled:opacity-40" data-testid="random-plus">+</button>
                  </div>
                  <p className="text-center text-[11px] text-slate-500 mt-3" data-testid="seats-available-hint">
                    {qtyCap < 1 ? "No seats available" : `${availableSeats} seat${availableSeats !== 1 ? "s" : ""} available${drop.max_per_user ? ` · max ${drop.max_per_user} per person` : ""}`}
                  </p>
                </div>

                {drop.ticket_price > 0 && (
                  <div className="flex items-center justify-between mt-4 pt-1">
                    <span className="text-slate-400 font-mono text-sm">{qty} × ${drop.ticket_price.toFixed(2)}</span>
                    <div className="text-right">
                      <span className="block text-[11px] uppercase tracking-widest text-slate-500 font-mono">Total</span>
                      <span className="text-gold font-display font-black text-3xl leading-none" data-testid="quick-total">${(qty * drop.ticket_price).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {drop.has_free_seats && (
                  <p className="text-[11px] text-emerald-400 mt-4 flex items-center gap-1" data-testid="free-seat-hint"><Gift className="w-3.5 h-3.5 shrink-0" /> {drop.free_seats > 0 ? `${drop.free_seats} free seat${drop.free_seats > 1 ? "s" : ""} hidden! ` : ""}Some seats are secretly free — land one and it's on the house!</p>
                )}

                <div className={`mt-4 grid gap-3 ${user && user.store_credit > 0 && drop.ticket_price > 0 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {user && user.store_credit > 0 && drop.ticket_price > 0 && (
                    <button onClick={() => askConfirm(true, qty)} disabled={busy || qtyCap < 1} className="h-14 rounded-xl card-surface hover:border-gold/40 flex items-center justify-center gap-2.5 px-3 disabled:opacity-50 transition-colors" data-testid="random-buy-credit">
                      <Wallet className="w-5 h-5 text-gold shrink-0" />
                      <span className="text-left leading-tight">
                        <span className="block font-bold text-white text-sm">Use Credit</span>
                        <span className="block text-[11px] text-slate-400 font-mono whitespace-nowrap">${user.store_credit.toFixed(2)} available</span>
                      </span>
                    </button>
                  )}
                  <button onClick={() => askConfirm(false, qty)} disabled={busy || qtyCap < 1} className="h-14 rounded-xl gold-gradient text-obsidian font-bold px-4 flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap" data-testid="random-buy">
                    <Shuffle className="w-5 h-5 shrink-0" /> {qtyCap < 1 ? "Sold out" : drop.ticket_price > 0 ? "Surprise me" : `Claim ${qty} seat${qty > 1 ? "s" : ""}`}
                  </button>
                </div>
                <p className="text-center text-[11px] text-slate-500 mt-4 flex items-center justify-center gap-1"><ShieldCheck className="w-3 h-3 text-gold/60" /> Winners drawn with true randomness from RANDOM.ORG</p>
              </div>
            </div>
          ) : (
            /* Pick Seats grid */
            <div>
              <div className="flex items-center justify-end mb-3">
                <div className="flex items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full card-surface" /> Open</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gold" /> Selected</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-vault" /> Taken</span>
                </div>
              </div>
              <div className="grid grid-cols-6 sm:grid-cols-10 gap-2 p-4 bg-vault/60 rounded-2xl border border-gold/15 max-h-[420px] overflow-y-auto custom-scrollbar" data-testid="seat-grid">
                {drop.seats.map((s) => (
                  <button key={s.n} onClick={() => toggleSeat(s)} disabled={s.status !== "available"} data-testid={`seat-${s.n}`}
                    className={`relative overflow-hidden aspect-square rounded-full text-xs font-mono font-bold flex items-center justify-center transition-[background-color,transform,border-color] ${seatColor(s)}`}>
                    {s.holder_avatar ? <img src={fileUrl(s.holder_avatar)} alt={s.holder} title={s.holder} className="absolute inset-0 w-full h-full object-cover" /> : s.n}
                    {s.holder_avatar && s.mine && <span className="absolute inset-0 ring-2 ring-emerald-400 rounded-full" />}
                  </button>
                ))}
              </div>

              {/* checkout bar */}
              <div className="mt-5 card-surface rounded-2xl p-4 sm:p-5 sticky bottom-[calc(5rem+env(safe-area-inset-bottom))] md:bottom-4 glass z-40" data-testid="checkout-bar">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div>
                    <p className="text-slate-400 text-[11px] uppercase tracking-widest font-mono">{selected.length} seat{selected.length !== 1 ? "s" : ""} selected</p>
                    <p className="text-3xl font-display font-black text-gold leading-none mt-1" data-testid="checkout-total">{charged > 0 ? `$${charged.toFixed(2)}` : "FREE"}</p>
                  </div>
                  {drop.has_free_seats && selected.length > 0 && (
                    <p className="text-[11px] text-emerald-400 flex items-center gap-1 text-right max-w-[45%]" data-testid="free-seat-hint-grid"><Gift className="w-3.5 h-3.5 shrink-0" /> {drop.free_seats > 0 ? `${drop.free_seats} free seats hidden!` : "Some seats are secretly free!"}</p>
                  )}
                </div>
                <div className={`grid gap-3 ${user && user.store_credit > 0 && charged > 0 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {user && user.store_credit > 0 && charged > 0 && (
                    <button onClick={() => askConfirm(true)} disabled={busy || !selected.length || user.store_credit < charged}
                      title={user.store_credit < charged ? `Not enough credit ($${user.store_credit.toFixed(2)})` : ""}
                      className="h-14 rounded-xl card-surface hover:border-gold/40 flex items-center justify-center gap-2.5 px-3 disabled:opacity-40 transition-colors" data-testid="pay-with-credit">
                      <Wallet className="w-5 h-5 text-gold shrink-0" />
                      <span className="text-left leading-tight">
                        <span className="block font-bold text-white text-sm">Use Credit</span>
                        <span className="block text-[11px] text-slate-400 font-mono">${user.store_credit.toFixed(2)} available</span>
                      </span>
                    </button>
                  )}
                  <button onClick={() => askConfirm(false)} disabled={busy || !selected.length} className="h-14 rounded-xl gold-gradient text-obsidian font-bold flex items-center justify-center gap-2 px-4 disabled:opacity-50 group-hover:brightness-110 transition-all" data-testid="stripe-checkout-button">
                    {charged > 0 ? <><CreditCard className="w-5 h-5" /> Pay with Card</> : <><Gift className="w-5 h-5" /> Claim free seats</>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Entrants list */}
      {!drop.is_referral && entrants.length > 0 && (
        <div className="mt-10 card-surface rounded-2xl p-6" data-testid="entrants-section">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-gold" />
            <h2 className="font-display text-xl font-bold text-white">Entrants ({entrants.length})</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {(showAllEntrants ? entrants : entrants.slice(0, 6)).map((e, i) => (
              <div key={i} className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-vault/60 border border-gold/10" data-testid={`entrant-${i}`}>
                {e.avatar ? (
                  <img src={fileUrl(e.avatar)} alt={e.name} className="w-10 h-10 rounded-full object-cover shrink-0" />
                ) : (
                  <span className="w-10 h-10 rounded-full gold-gradient text-obsidian font-black flex items-center justify-center shrink-0">{(e.name || "T")[0].toUpperCase()}</span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-sm truncate">{e.name}</p>
                  <p className="text-slate-400 text-xs font-mono truncate">Seats: {e.seats.map((n) => `#${n}`).join(", ")}</p>
                  {e.free_seats.length > 0 && (
                    <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wide text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 rounded px-1.5 py-0.5" data-testid={`entrant-free-${i}`}>
                      <Gift className="w-3 h-3" /> Free {e.free_seats.map((n) => `#${n}`).join(", ")}
                    </span>
                  )}
                </div>
                {isAdmin && e.user_id && drop.status !== "drawn" && (
                  confirmRemove === e.user_id ? (
                    <div className="flex items-center justify-end gap-1.5 w-full" data-testid={`entrant-confirm-${i}`}>
                      {drop.ticket_price > 0 && !drop.is_referral && <button onClick={() => removeEntry(e.user_id, true)} disabled={busy} className="text-[11px] font-bold gold-gradient text-obsidian rounded-lg px-2.5 py-1.5 disabled:opacity-50 whitespace-nowrap" data-testid={`entrant-confirm-refund-${i}`}>Remove + refund</button>}
                      <button onClick={() => removeEntry(e.user_id)} disabled={busy} className="text-[11px] font-bold bg-red-500/90 text-white rounded-lg px-2.5 py-1.5 disabled:opacity-50" data-testid={`entrant-confirm-remove-${i}`}>{drop.ticket_price > 0 && !drop.is_referral ? "Remove" : "Confirm"}</button>
                      <button onClick={() => setConfirmRemove(null)} disabled={busy} className="text-[11px] font-semibold card-surface border border-gold/20 text-slate-300 rounded-lg px-2.5 py-1.5" data-testid={`entrant-cancel-remove-${i}`}>Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmRemove(e.user_id)} className="shrink-0 text-slate-500 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-500/10 transition-colors" title="Remove entry" data-testid={`entrant-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  )
                )}
              </div>
            ))}
          </div>
          {entrants.length > 6 && (
            <button onClick={() => setShowAllEntrants((v) => !v)} className="mt-4 w-full card-surface rounded-xl py-2.5 text-sm font-semibold text-gold hover:border-gold/40 flex items-center justify-center gap-1.5" data-testid="show-all-entrants">
              {showAllEntrants ? "Show less" : `Show all ${entrants.length}`} <ChevronDown className={`w-4 h-4 transition-transform ${showAllEntrants ? "rotate-180" : ""}`} />
            </button>
          )}
          <p className="text-center text-[11px] text-slate-500 mt-4 flex items-center justify-center gap-1"><ShieldCheck className="w-3 h-3 text-gold/60" /> Winners drawn with true randomness from RANDOM.ORG</p>
        </div>
      )}
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />

      {/* Commit / confirmation step — full price shown, free status hidden until after purchase */}
      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => !busy && setConfirm(null)} data-testid="confirm-modal">
          <div className="w-full max-w-md card-surface rounded-3xl p-6 glass animate-rise" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-black text-white text-center">Confirm your entry</h3>
            <p className="text-slate-400 text-sm text-center mt-1">Review and purchase to lock in your {confirm.count === 1 ? "seat" : "seats"}.</p>

            <div className="mt-5 rounded-2xl bg-obsidian/50 border border-gold/10 p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">{confirm.randomCount ? "Surprise seats" : "Your seats"}</span>
                <span className="text-white font-mono font-semibold truncate max-w-[60%] text-right">{confirm.randomCount ? `${confirm.count} random` : selected.map((n) => `#${n}`).join(", ")}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Entry fee</span>
                <span className="text-white font-mono">${drop.ticket_price.toFixed(2)} × {confirm.count}</span>
              </div>
              <div className="h-px bg-gold/10" />
              <div className="flex items-center justify-between">
                <span className="text-slate-300 font-semibold">Total</span>
                <span className="text-gold font-display font-black text-2xl" data-testid="confirm-total">${(drop.ticket_price * confirm.count).toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-400">
              {confirm.useCredit ? <><Wallet className="w-4 h-4 text-gold" /> Paying with store credit</> : <><CreditCard className="w-4 h-4 text-gold" /> Paying by card</>}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <button onClick={() => setConfirm(null)} disabled={busy} className="h-12 rounded-xl card-surface text-slate-200 font-semibold hover:border-gold/30 disabled:opacity-50" data-testid="confirm-cancel">Cancel</button>
              <button onClick={runCheckout} disabled={busy} className="h-12 rounded-xl gold-gradient text-obsidian font-black flex items-center justify-center gap-2 disabled:opacity-60" data-testid="confirm-purchase">
                {busy ? "Processing…" : "Purchase"}
              </button>
            </div>
            <p className="text-[10px] text-slate-500 text-center mt-3">Some seats are secretly free — if you land one, you won't be charged for it.</p>
          </div>
        </div>
      )}

      {/* Bonus-seat reveal (shown only after the user commits) */}
      {reveal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setReveal(null)} data-testid="bonus-reveal">
          <div className="w-full max-w-sm card-surface rounded-3xl p-8 glass text-center animate-rise border-gold/40" onClick={(e) => e.stopPropagation()}>
            <div className="w-20 h-20 rounded-full gold-gradient flex items-center justify-center mx-auto gold-glow animate-pulse"><Gift className="w-10 h-10 text-obsidian" /></div>
            <h3 className="font-display text-3xl font-black text-gold mt-5">Bonus Seat{reveal.seats.length > 1 ? "s" : ""}!</h3>
            <p className="text-white text-lg mt-2">
              Seat{reveal.seats.length > 1 ? "s" : ""} {reveal.seats.map((n) => `#${n}`).join(", ")} {reveal.seats.length > 1 ? "were" : "was"} a bonus — you got {reveal.seats.length > 1 ? "them" : "it"} <b className="text-gold">free of charge!</b>
            </p>
            <p className="text-slate-400 text-sm mt-2">You're entered in the draw. Good luck! 🎉</p>
            <button onClick={() => setReveal(null)} className="mt-6 w-full h-12 rounded-xl gold-gradient text-obsidian font-black" data-testid="bonus-reveal-close">Awesome!</button>
          </div>
        </div>
      )}
    </div>
  );
}
